import React, { useEffect, useState } from 'react'
import { PolaroidList } from 'components/SA/Polaroid'
import ListConnector, { ConnectorProps as ListProps } from '../../connectors/List'
import styles from './UserPolaroids.module.scss'
import SALink from 'components/SALink'
import { PolaroidCompleteSkeleton } from 'components/SA/Polaroid/components/PolaroidSkeleton'
import { ARTWORK_STATUS } from 'components/SA/Polaroid/helpers'
import { useSelector } from 'react-redux'
import { getTotalResults } from 'selectors/searchProvider'
import { getFullName } from 'apps/public/profile/artistProfile/selectors'
import { SAArtwork } from 'types/Artwork'
type UserPolaroidListProps = ListProps & {
  isFiltersVisible?: boolean
  artworks: ReadonlyArray<SAArtwork>
  isLoading?: boolean
  searchQuery: string | null
  isAtLeastOneFilterSelected: boolean
}

const UserPolaroidList = ({
  artworks,
  isReady,
  isSet,
  isUsersArtworksAccount,
  userID,
  isFiltersVisible,
  isAtLeastOneFilterSelected,
}: UserPolaroidListProps) => {
  const [isWaitingConstructor, setIsWaitingConstructor] = useState(true)
  const totalNumberOfResults = useSelector(getTotalResults)
  const artistName = useSelector(getFullName)

  const handleCheckIsPrintForDataLayerEvent = ({ originalStatus, hasOpenEditions }) => {
    const isSold = originalStatus === ARTWORK_STATUS.SOLD
    const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
    return (isSold || isUnavailable) && hasOpenEditions
  }

  useEffect(() => {
    setIsWaitingConstructor(true)

    const timer = setTimeout(() => {
      if (!isAtLeastOneFilterSelected && !artworks.length) {
        setIsWaitingConstructor(false)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [artworks])

  if (isAtLeastOneFilterSelected && !artworks.length) {
    return (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4'>
          Sorry, there are no artworks that meet your selection criteria. Try to remove some filters
          to see more results.
        </h4>
      </div>
    )
  }

  if (isUsersArtworksAccount && !artworks.length) {
    return (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4 defaultTextCapitalize'>Your Portfolio Is Empty.</h4>
        <SALink className='defaultButton' href='/upload'>
          UPLOAD ARTWORKS NOW
        </SALink>
      </div>
    )
  }

  if (!artworks.length) {
    return isWaitingConstructor ? (
      <div className={styles.polaroidListContainer}>
        {Array.from({ length: 6 }, (_, index) => (
          <PolaroidCompleteSkeleton key={index.toString()} />
        ))}
      </div>
    ) : (
      <div className={styles.noResults}>
        <h4 className='defaultTypographyH4 defaultTextCapitalize'>
          {artistName} has not uploaded any artworks yet.
        </h4>
        <SALink className='defaultButton' href='/all'>
          BROWSE ARTWORKS
        </SALink>
      </div>
    )
  }

  return (
    <PolaroidList
      dataApi='constructor'
      dataList='artist-profile-all-artworks'
      dataListId='artist-profile-all-artworks'
      dataListName={`${artistName} Artworks`}
      artworks={artworks}
      imageSuffix='noSuffix'
      isLoading={!isReady || !isSet}
      isArtistNameVisible={false}
      userID={userID}
      shouldSendAlgoliaEvents={false}
      isUsersArtworksAccount={isUsersArtworksAccount}
      customStyle={!isFiltersVisible ? styles.customPolaroidListContainer : ''}
      onCheckIsPrintForDataLayerEvent={handleCheckIsPrintForDataLayerEvent}
      searchProviderAttributes={[
        { 'browse-container': true },
        { 'total-number-results': totalNumberOfResults },
        { 'filter-name': 'group_id' },
        { 'filter-value': 'all' },
      ]}
    />
  )
}

export default ListConnector(UserPolaroidList)
